import { match, P } from 'ts-pattern';
import { EvaluationValue, EvaluationValueKind } from '../types';

/**
 * Transform an evaluation value into a display representation.
 */
export const formatEvaluationValue = (value: EvaluationValue): string => {
  return match(value)
    .with({ kind: EvaluationValueKind.Empty }, () => 'Empty output')
    .with({ kind: EvaluationValueKind.Boolean }, ({ value }) =>
      value ? 'True' : 'False'
    )
    .with({ kind: EvaluationValueKind.Number }, ({ value }) =>
      new Intl.NumberFormat().format(Number(value ?? 0))
    )
    .with({ kind: EvaluationValueKind.Object }, ({ value }) =>
      JSON.stringify(value ?? {}, null, 2)
    )
    .with({ kind: EvaluationValueKind.String }, ({ value }) =>
      String(value ?? '')
    )
    .with({ kind: EvaluationValueKind.Array }, ({ value }) =>
      JSON.stringify(value ?? [], null, 2)
    )
    .exhaustive();
};

/**
 * Transform an incoming value from the backend into an evaluation value.
 */
export const toEvaluationValue = (value: unknown): EvaluationValue =>
  match(value)
    .with(P.nullish, () => ({ kind: EvaluationValueKind.Empty }))
    .with(P.array(), (value) => ({ kind: EvaluationValueKind.Array, value }))
    .with(P.string, (value) => ({ kind: EvaluationValueKind.String, value }))
    .with(P.boolean, (value) => ({ kind: EvaluationValueKind.Boolean, value }))
    .with(P.number, (value) => ({ kind: EvaluationValueKind.Number, value }))
    .with(
      P.when((v) => typeof v === 'object'),
      (value) => ({
        kind: EvaluationValueKind.Object,
        value,
      })
    )
    .otherwise(() => ({ kind: EvaluationValueKind.Empty })) as EvaluationValue;
